import React, { useMemo, useState } from 'react'

import { Box, ProgressBar, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Dialog, F0Icon, F0Select, F0TagRaw } from '@factorialco/f0-react'
import { ModuleId, OneEmptyState, Tooltip } from '@factorialco/f0-react/dist/experimental'
import { ChevronDown, ChevronUp, EyeInvisible, GroupBy } from '@factorialco/f0-react/icons/app'
import { SUPPORTED_LANGUAGES, SUPPORTED_LANGUAGES_LABELS } from 'config/locales'
import { FormFeaturesDocument, type FormFeaturesQuery } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useAnswerForm } from 'modules/forms/hooks/useAnswerForm'
import { useLoadForm } from 'modules/forms/hooks/useForms'
import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'
import { Answer } from 'modules/forms/lib/types'
import { useWithoutDisabledLocales } from 'modules/settings/components/LanguageSelector'

import { ElementPreview } from './preview/ElementPreview'

function hasFeature(data: FormFeaturesQuery | undefined, featureName: string) {
  if (!data) {
    return false
  }

  return (
    data?.features.featuresConnection.nodes.some(
      (feature) => feature.name === featureName && feature.enabled
    ) ?? false
  )
}

interface FormRenderProps {
  form?: Form
  mode: 'preview' | 'answer'
  formId?: string
  resourceType?: string
  isOpen: boolean
  membershipId?: string
  onClose: () => void
  onSubmitSuccess?: () => void
  resourceHeaderModule?: {
    id: ModuleId
    label: string
    href: string
  }
}

function isSectionElement(el: Element | SectionElement): el is SectionElement {
  return 'elements' in el && Array.isArray(el.elements)
}

// This function is use for having all the elements at the same level, avoiding the nesting that the sections can have of the elements
function flattenElements(elements: Element[] | SectionElement[]): Element[] {
  const flat: Element[] = []

  for (const el of elements) {
    if (isSectionElement(el)) {
      flat.push(el)
      flat.push(...flattenElements(el.elements))
    } else {
      flat.push(el)
    }
  }

  return flat
}

const FormRender: React.FC<FormRenderProps> = ({
  form,
  formId,
  resourceType,
  isOpen,
  onClose,
  onSubmitSuccess,
  resourceHeaderModule,
  membershipId,
  mode,
}) => {
  const { company } = useCurrentSession()
  const [updateDisabled, setUpdateDisabled] = useState(false)
  const [locale, setLocale] = useState('en')

  const { data: featuresData } = useQuery(FormFeaturesDocument, {
    variables: { companyId: company.id },
    fetchPolicy: 'cache-first',
  })

  const hasTranslationsEnabled = hasFeature(featuresData, 'forms_translations')

  const supportedLanguages: string[] = useWithoutDisabledLocales(SUPPORTED_LANGUAGES)
  const { form: loadedForm, loadingForm } = useLoadForm({
    formId,
    resourceType,
    locale: hasTranslationsEnabled ? locale : undefined,
  })
  form ||= loadedForm
  // Reset currentElementIndex and form answers when formId changes or modal opens
  React.useEffect(() => {
    setCurrentElementIndex(0)
    form?.resetFormAnswers()
  }, [formId, isOpen])

  // If the component is in answers mode, the user can answer the questions so we use this function to update the element with the answer
  const handleAnswer = (answer: Answer, elementId: string) => {
    //This is use to update the value of the hasUnansweredMandatoryElements
    setUpdateDisabled(true)
    const element = form?.getElement(elementId)
    if (element) element.answer = answer
  }

  // We check the number of mandatory questions that hasn't been answer yet
  const hasUnansweredMandatoryElements = useMemo(() => {
    setUpdateDisabled(false)
    return form?.hasUnansweredMandatoryElements() ?? false
  }, [updateDisabled, form])

  const [currentElementIndex, setCurrentElementIndex] = useState<number>(0)
  const elements = form ? flattenElements(form?.elements) : []

  const currentElement = useMemo(() => {
    return elements[currentElementIndex]
  }, [currentElementIndex, form?.elements])

  const { saveAnswers, savingAnswers } = useAnswerForm()

  // If the component is in answers mode, the user can answer the questions so we use this function to call the backend mutation to store the answers
  const onSubmit = async () => {
    if (!form || !membershipId) return
    await saveAnswers({
      form,
      membershipId,
      onSuccess: (answers) => {
        app.setFlash({
          type: 'correct',
          message: i18n.t('forms.render.answers_saved'),
        })
        //This resets the forms answers and set the currentElement back to the first one
        form?.resetFormAnswers()
        setCurrentElementIndex(0)
        onClose()
        onSubmitSuccess?.()
      },
    })
  }

  const getButtonLabel = () => {
    if (currentElementIndex === elements.length - 1) return i18n.t('forms.render.button_submit')
    return i18n.t('forms.render.button_next')
  }

  const isButtonDisabled = () => {
    if (savingAnswers) return true
    if (currentElementIndex === elements.length - 1) {
      //Mode answer is used when employees are answering the survey and in Editor preview
      if (mode === 'answer') {
        return hasUnansweredMandatoryElements || membershipId === undefined
      }
      return true // Always disabled in preview/response mode
    }
    // For Next button, check if current element is mandatory and unanswereds
    const currentElement = elements[currentElementIndex]
    const isSectionElement = currentElement?.getType() === 'section'

    if (currentElement?.mandatory && !isSectionElement && mode === 'answer') {
      const answer = form?.getElement(currentElement.id)?.answer
      return !answer || !answer.answer || answer.answer.trim() === ''
    }

    return false
  }

  const getTooltipDescription = () => {
    if (currentElementIndex === elements.length - 1) {
      //Mode answer is used when employees are answering the survey and in Editor preview
      if (mode === 'answer') {
        return hasUnansweredMandatoryElements ? i18n.t('forms.render.submit_mandatory_pending') : ''
      }
      return i18n.t('forms.render.submit_disabled_preview')
    }

    // Tooltip for Next button when current element is mandatory and unanswered
    const currentElement = elements[currentElementIndex]
    if (currentElement?.mandatory && mode === 'answer') {
      const answer = form?.getElement(currentElement.id)?.answer
      if (!answer || !answer.answer || answer.answer.trim() === '') {
        return i18n.t('forms.render.mandatory_pending')
      }
    }

    return ''
  }

  const handleButtonClick = () => {
    if (currentElementIndex === elements.length - 1) {
      if (mode === 'answer') {
        onSubmit()
      }
    } else {
      setCurrentElementIndex((prev) => prev + 1)
    }
  }

  const handleBackClick = () => {
    setCurrentElementIndex((prev) => prev - 1)
  }

  return (
    <F0Dialog
      onClose={onClose}
      title={form?.title}
      isOpen={isOpen}
      position="fullscreen"
      module={resourceHeaderModule}
    >
      {loadingForm ? (
        <Loading expand />
      ) : !form ? (
        <Box height="full" justifyContent="center" alignItems="center" paddingX="s16">
          <OneEmptyState
            emoji="📝"
            title={i18n.t('forms.render.empty.title')}
            description={i18n.t('forms.render.empty.description')}
          />
        </Box>
      ) : form.elements?.length === 0 ? (
        <Box height="full" justifyContent="center" alignItems="center" paddingX="s16">
          <OneEmptyState
            emoji="📝"
            title={i18n.t('forms.render.empty.title')}
            description={i18n.t('forms.render.empty.description')}
          />
        </Box>
      ) : (
        <Box
          width="full"
          flexDirection="column"
          height="full"
          style={{ minHeight: '80vh' }}
          padding="s24"
        >
          <Box
            width="full"
            flexDirection="row"
            justifyContent="spaceBetween"
            alignItems="center"
            position="absolute"
            style={{ right: '18px', top: '18px' }}
            gap="s24"
          >
            <Box flexDirection="row" alignItems="center" gap="s16">
              {currentElement?.sectionId !== undefined && (
                <F0TagRaw
                  icon={GroupBy}
                  text={elements.find((el) => el.id === currentElement?.sectionId)?.label || '-'}
                />
              )}
            </Box>
            {hasTranslationsEnabled && (
              <F0Select
                label="Language"
                options={supportedLanguages.map((locale) => ({
                  value: locale,
                  label: SUPPORTED_LANGUAGES_LABELS[locale] || locale,
                }))}
                value={locale}
                onChange={(value: string) => setLocale(value)}
              />
            )}
            <Box gap="s8" flexDirection="row" alignItems="center">
              <F0Button
                icon={ChevronUp}
                onClick={handleBackClick}
                disabled={currentElementIndex === 0}
                variant="outline"
                size="sm"
                hideLabel
              />
              <F0Button
                icon={ChevronDown}
                onClick={handleButtonClick}
                disabled={isButtonDisabled()}
                variant="outline"
                size="sm"
                hideLabel
              />
            </Box>
          </Box>
          <Box
            width="full"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="s24"
            style={{ flex: '1', minHeight: '0' }}
          >
            <Box
              maxWidth="s600"
              width="full"
              gap="s24"
              paddingX="s16"
              alignItems="flexStart"
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              {currentElement && (
                <ElementPreview element={currentElement} mode={mode} handleAnswer={handleAnswer} />
              )}
              <Box flexDirection="row" gap="s8" alignItems="center">
                {isButtonDisabled() ? (
                  <Tooltip description={getTooltipDescription()}>
                    <span>
                      <F0Button
                        label={getButtonLabel()}
                        disabled
                        onClick={handleButtonClick}
                        size="md"
                        variant="default"
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <F0Button
                    label={getButtonLabel()}
                    onClick={handleButtonClick}
                    size="md"
                    variant="default"
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {form?.elements?.length !== 0 ? (
        <Box
          width="full"
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          padding="s16"
          position="absolute"
          style={{ bottom: '0px', left: '0px' }}
          border={{ top: { width: 's1', style: 'solid', color: 'grey300' } }}
        >
          {form?.anonymous && (
            <Box
              flexDirection="row"
              alignItems="center"
              padding="s8"
              background="grey300"
              gap="s4"
              borderRadius={{ all: 'abs012' }}
            >
              <F0Icon icon={EyeInvisible} />
              <Text.Normal>{i18n.t('forms.render.visibility')}</Text.Normal>
            </Box>
          )}

          <Box flexDirection="row" alignItems="center" gap="s16" width="s200">
            <ProgressBar amount={currentElementIndex + 1} total={elements.length} showLabel />
          </Box>
        </Box>
      ) : null}
    </F0Dialog>
  )
}

export default FormRender
